import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

/* ================= Helpers ================= */

const formatearFecha = (fechaStr) => {
  if (!fechaStr) return "—";
  const fecha = new Date(fechaStr);
  if (Number.isNaN(fecha.getTime())) return "—";
  return fecha.toLocaleDateString("es-CO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

/* ================= Component ================= */

const ListarActasPorGrupo = ({ refresh }) => {
  const { id } = useParams(); // id del grupo (o fichaGrupo según tu ruta)
  const navigate = useNavigate();

  const [actas, setActas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /* ================= Descargar ================= */

  const generarYDescargarActa = useCallback(async (idActa, fichaGrupo, numeroActa) => {
    try {
      setDownloadingId(idActa);

      const response = await axios.get(`/api/actas/generar-docx/${idActa}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Acta_${fichaGrupo}_${numeroActa}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al generar el acta:", error.message);
      Swal.fire("Error", "No se pudo generar el acta.", "error");
    } finally {
      setDownloadingId(null);
    }
  }, []);

  /* ================= Eliminar ================= */

  const deleteActa = useCallback(async (idActa) => {
    try {
      const confirm = await Swal.fire({
        title: "¿Está seguro?",
        text: "¡Esta acción no se puede deshacer!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (!confirm.isConfirmed) return;

      setDeletingId(idActa);

      const { data } = await axios.delete(`/api/grupos/actas/delete/${idActa}`);

      // ✅ NO refetch: quitamos del estado local
      setActas((prev) => prev.filter((a) => a._id !== idActa));

      Swal.fire({
        icon: "success",
        title: data?.message || "Acta eliminada",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Error al eliminar el acta:", error.message);
      Swal.fire("Error", "No se pudo eliminar el acta.", "error");
    } finally {
      setDeletingId(null);
    }
  }, []);

  /* ================= Cargar datos (UNA sola consulta) ================= */

  const getData = useCallback(async () => {
    try {
      if (!id) return;

      setLoading(true);

      // ✅ UNA SOLA CONSULTA
      const { data } = await axios.get(`/api/grupos/actas/listidgrupo/${id}`);

      const lista = data?.ok && Array.isArray(data.actas) ? data.actas : [];

      const dataFormateada = lista
        .map((acta) => ({
          ...acta,
          fecha: formatearFecha(acta.fecha),
          funciones: (
            <div className="d-flex gap-2 flex-nowrap justify-content-end align-items-center">
              {/* Descargar */}
              <button
                className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => generarYDescargarActa(acta._id, acta.fichaGrupo, acta.numeroActa)}
                title="Descargar"
                type="button"
                disabled={loading || deletingId === acta._id || downloadingId === acta._id}
              >
                {downloadingId === acta._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-file-word text-white"></i>
                )}
              </button>

              {/* Editar */}
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => navigate(`/institucional/actas/editar/${acta._id}`)}
                title="Editar"
                type="button"
                disabled={loading || deletingId === acta._id || downloadingId === acta._id}
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>

              {/* Eliminar */}
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => deleteActa(acta._id)}
                title="Eliminar"
                type="button"
                disabled={loading || deletingId === acta._id || downloadingId === acta._id}
              >
                {deletingId === acta._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-trash"></i>
                )}
              </button>
            </div>
          ),
        }))
        .reverse();

      setActas(dataFormateada);
    } catch (error) {
      console.error("Error al cargar las actas:", error.message);
      setActas([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // ✅ SOLO id

  /* ================= Effects ================= */

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  /* ================= Columns ================= */

  const columns = useMemo(
    () => [
      { Header: "N° Acta", accessor: "numeroActa" },
      { Header: "Comité o Reunión", accessor: "nombreComiteReunion" },
      { Header: "Ciudad", accessor: "ciudad" },
      { Header: "Fecha", accessor: "fecha" },
      { accessor: "funciones" },
    ],
    []
  );

  /* ================= Render ================= */

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Actas del grupo</h5>
                <small className="text-secondary">Administración y descarga de actas.</small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                  title="Actualizar"
                >
                  <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"} me-2`}></i>
                  Actualizar
                </button>

                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate(`/institucional/actas/nuevo/${id}`)}
                  disabled={loading}
                  title="Registrar Acta"
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Nuevo
                </button>


              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" aria-hidden="true"></div>
                <div className="mt-3">Cargando actas…</div>
              </div>
            ) : Array.isArray(actas) && actas.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={actas} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">📄</div>
                <div className="fw-semibold">No hay actas registradas</div>
                <div className="small mb-3">Crea la primera acta para este grupo.</div>
                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate(`/institucional/actas/nuevo/${id}`)}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Crear acta
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarActasPorGrupo;
